import {
  type ParameterRecord,
  unpackParameterRecord,
} from '@robovision/data'
import type {
  HubCreateDeployment,
  HubDeletedDeployment,
  HubDeployment,
  HubDeploymentDependencyRefs,
  HubExampleStream,
  HubResource,
  HubUpdateDeployment,
} from '@robovision/hub'
import {
  addOrReplaceBy,
  DateTemplates,
  formatDate,
  HttpStatusCode,
  sortByDate,
} from '@robovision/rvai-utils'
import type { AxiosResponse } from 'axios'
import {
  differenceInMinutes,
  parseISO,
} from 'date-fns'
import { saveAs } from 'file-saver'
import { defineStore } from 'pinia'
import { LocalStorage } from 'quasar'
import {
  reactive,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import type { ErrorData } from '~domain/Errors'
import { Inference } from '~domain/inference/Inference'
import { InferenceApiEndpoint } from '~domain/inference/InferenceApiEndpoint'
import type { JobV2 } from '~domain/jobV2/Job'
import { LocalStorageKeys } from '~domain/localStorageKeys'
import type { InferenceJobData } from '~domain/projects/JobData'
import { hubApi } from '~services/api'
import { queryClient } from '~services/query-client'
import {
  acceptStatus,
  getResponseHeader,
  HttpHeaderName,
} from '~utils/api'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'
import {
  fetchInferencesQueryOptions,
  InferenceFilter,
} from '~utils/api/inferences'
import { useModels } from './composables/useModels'
import {
  buildJobDependencies,
  buildRuntimeParameters,
} from './projectsStore/helpers'
import { useProjectsStore } from './projectsStore/useProjectsStore'
import { useAlgorithmsStore } from './useAlgorithmsStore'
import { useCameraConfigurationsStore } from './useCameraConfigurationsStore'
import { useJobsStore } from './useJobsStore'


const DEFAULT_INFERENCE_LOGS_FILE_EXTENSION = 'txt'

function getInferenceLogsFileExtension (response: AxiosResponse) {
  const contentDisposition = getResponseHeader(response, HttpHeaderName.ContentDisposition)
  if (typeof contentDisposition !== 'string') return DEFAULT_INFERENCE_LOGS_FILE_EXTENSION

  const fileName = contentDisposition.split('filename=')
    .pop()

  if (!fileName) return DEFAULT_INFERENCE_LOGS_FILE_EXTENSION

  return fileName
    .replaceAll('"', '')
    .split('.')
    .pop() || DEFAULT_INFERENCE_LOGS_FILE_EXTENSION
}

export const useInferencesStore = defineStore('inferences', () => {
  const state = reactive({
    inferenceApiEndpoints: new Map<string, InferenceApiEndpoint[]>(),
    inferenceBlobs: new Map<string, HubResource[]>(),
    inferenceExampleStreams: new Map<string, HubExampleStream[]>(),
    inferenceJobs: new Map<string, JobV2 | undefined>(),
    inferenceJobsIds: new Map<string, string[]>(),
    inferenceModelIds: new Map<string, string[]>(),
    inferenceModels: new Map<string, Record<string, string | undefined>>(),
    inferenceParameters: new Map<string, Record<string, ParameterRecord>>(),
    inferences: new Map<string, Inference[]>(),
    loading: false as boolean,
  })

  const getters = {
    getInference (projectId?: string, inferenceId?: string): Inference | undefined {
      if (projectId) {
        if (inferenceId) {
          return state.inferences.get(projectId)?.find(v => v.id === inferenceId)
        }

        return state.inferences.get(projectId)?.at(0)
      } else {
        if (inferenceId) {
          return [...state.inferences.values()].flat().find(v => v.id === inferenceId)
        }

        return undefined
      }
    },

    getInferenceApiEndpoints (inferenceId?: string): InferenceApiEndpoint[] {
      if (!inferenceId) return []

      return (state.inferenceApiEndpoints.get(inferenceId) ?? []) as InferenceApiEndpoint[]
    },

    getInferenceBlob (inferenceId: string, artifactId: string): HubResource | undefined {
      const blobs = state.inferenceBlobs.get(inferenceId) ?? []

      return blobs.find(b => b.artifactId === artifactId)
    },

    getInferenceExampleStreams (inferenceId: string): HubExampleStream[] {
      return state.inferenceExampleStreams.get(inferenceId) ?? []
    },

    getInferenceJobsIds (inferenceId: string): string[] {
      return state.inferenceJobsIds.get(inferenceId) ?? []
    },

    getInferenceModelIds (inferenceId: string): string[] {
      return state.inferenceModelIds.get(inferenceId) ?? []
    },

    getInferenceModels (inferenceId: string): Record<string, string | undefined> {
      return state.inferenceModels.get(inferenceId) ?? {}
    },

    getInferenceParameters (inferenceId?: string): Record<string, ParameterRecord> {
      return inferenceId
        ? state.inferenceParameters.get(inferenceId) ?? {}
        : {}
    },

    getInferences (projectId: string): Inference[] {
      const inferences = state.inferences.get(projectId) ?? []
      return sortByDate(inferences, 'createdAt', false)
    },

    getInferencesFiltered (projectId: string, definitionIds: string[]): Inference[] {
      const inferences = (state.inferences.get(projectId) ?? [])
        .filter(i => definitionIds.includes(i.definitionId))

      return sortByDate(inferences, 'createdAt', false)
    },
  }

  const actions = {
    async checkInferenceExists (inferenceId: string, projectId?: string): Promise<Inference | undefined> {
      const inference = getters.getInference(projectId, inferenceId)
      if (inference) return inference

      const response = await hubApi.deployments.getDeployment(
        inferenceId,
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )
      if (!response?.data || response.data.id === ApiErrorId.DeploymentNotFound) return undefined

      return Inference.fromResponse(response.data)
    },

    async deleteInference (projectId: string, inferenceId: string): Promise<void> {
      try {
        state.loading = true
        const { data } = await hubApi.deployments.deleteDeployment(inferenceId)

        state.inferences.set(
          projectId,
          (state.inferences.get(projectId) ?? []).filter(v => v.id !== data.id),
        )
      } finally {
        state.loading = false
      }
    },

    async downloadLogs (inferenceId: string): Promise<void> {
      try {
        state.loading = true
        const response = await hubApi.deployments.getDeploymentLogs(
          inferenceId,
          { allLogs: true },
          {
            format: 'arraybuffer',
            validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
          },
        )

        // Just throw the error for displaying it in a toast message
        if (response.status === HttpStatusCode.NOT_FOUND) {
          const text = await new Blob([response.data]).text()
          const parsedData = JSON.parse(text)
          throw new Error((parsedData as ErrorData).message)
        }

        const blob = new Blob([response.data], { type: 'octet/stream' })
        const date = formatDate(new Date(), DateTemplates.Long)
        const fileExtension = getInferenceLogsFileExtension(response)
        const fileName = `inference_logs_${date}.${fileExtension}`

        saveAs(blob, fileName)
      } finally {
        state.loading = false
      }
    },

    async getJobDataExampleStream (
      jobData: InferenceJobData,
    ): Promise<HubExampleStream | undefined> {
      const { getCameraConfigurationById } = useCameraConfigurationsStore()

      if (jobData.cameraConfigurationId) {
        const cameraConfiguration = getCameraConfigurationById(jobData.cameraConfigurationId)
        if (cameraConfiguration) return cameraConfiguration.toExampleStream()
      }

      return undefined
    },

    handleDeleteInferenceEvent (deletedInference: HubDeletedDeployment): void {
      const entry = [...state.inferences.entries()]
        .find(([, inferences]) => inferences.filter((inference) => inference.id === deletedInference.id))
      if (!entry) return

      const [projectId, inference] = entry
      state.inferences.set(projectId, inference.filter(inference => inference.id !== deletedInference.id))
    },

    handleInferenceEvent (v: HubDeployment): void {
      const inference = Inference.fromResponse(v)

      if (inference.projectId) {
        state.inferences.set(
          inference.projectId,
          addOrReplaceBy(
            state.inferences.get(inference.projectId) ?? [],
            inference,
            v => v.id === inference.id,
          ),
        )
        state.loading = false
      }
    },

    async loadAllInferences (filter: InferenceFilter[] = []): Promise<Inference[]> {
      try {
        state.loading = true

        const { items: inferences } = await queryClient.fetchQuery(
          fetchInferencesQueryOptions({ filter }),
        )
        for (const inference of inferences) {
          if (!inference.projectId) continue

          state.inferences.set(
            inference.projectId,
            addOrReplaceBy(
              state.inferences.get(inference.projectId) ?? [],
              inference,
              v => v.id === inference.id,
            ),
          )
        }

        return inferences
      } finally {
        state.loading = false
      }
    },

    async loadInferenceApiEndpoints (inferenceId: string, params?: { signal?: AbortSignal }): Promise<void> {
      if (state.inferenceApiEndpoints.has(inferenceId)) return

      const { data } = await queryClient.fetchQuery({
        queryFn: ({ signal }) => hubApi.deployments.getDeploymentApiEndpoints(inferenceId, {
          signal: AbortSignal.any([params?.signal, signal].filter(Boolean)),
        }),
        queryKey: ['getDeploymentApiEndpoints', inferenceId],
      })
      const endpoints = data.map(endpoint => InferenceApiEndpoint.fromResponse(endpoint))
      state.inferenceApiEndpoints.set(inferenceId, endpoints)
    },

    async loadInferenceDependencies (
      inferenceId: string,
      loadParameters?: boolean,
    ): Promise<void> {
      const { getJob } = useJobsStore()
      const dependencies = await queryClient.fetchQuery({
        queryFn: () => hubApi.deployments.getDeploymentDependencies(inferenceId, {
          ...RETHROW_ERROR_AFTER_HANDLING,
          validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
        }),
        queryKey: ['getDeploymentDependencies', inferenceId],
      })

      const modelResources = Object.values(dependencies.data.models)
      const jobIds = modelResources
        .map(v => v.sourceJobId)
        .filter(Boolean)

      state.inferenceJobsIds.set(inferenceId, jobIds)

      state.inferenceExampleStreams.set(inferenceId, Object.values(dependencies.data.exampleStreams))
      state.inferenceBlobs.set(inferenceId, Object.values(dependencies.data.blobs))

      const modelIds = modelResources
        .map(v => v.id)
        .filter(Boolean)
      state.inferenceModelIds.set(inferenceId, modelIds)

      const models = Object.entries(dependencies.data.models).reduce((acc, [id, v]) => ({
        ...acc,
        [id]: v.id,
      }), {})
      state.inferenceModels.set(inferenceId, models)

      const [modelJobId] = jobIds
      if (modelJobId) {
        state.inferenceJobs.set(inferenceId, getJob(modelJobId))
      }

      if (loadParameters) {
        try {
          for (const [key, resource] of Object.entries(dependencies.data.parameters)) {
            const response = await queryClient.fetchQuery({
              queryFn: () => hubApi.resources.getParametersResourceData(
                resource.id,
                undefined,
                {
                  ...RETHROW_ERROR_AFTER_HANDLING,
                  validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
                },
              ),
              queryKey: ['getParametersResourceData', resource.id],
            })
            if (response.status === HttpStatusCode.NOT_FOUND) {
              console.error(`Parameters resource ${resource.id} not found`)
              continue
            }
            const { data: dataBuffer } = response
            const parameterRecord = await unpackParameterRecord(dataBuffer)

            state.inferenceParameters.set(inferenceId, {
              ...state.inferenceParameters.get(inferenceId),
              [key]: parameterRecord,
            })
          }
        } catch (e) {
          console.error('Failed to load parameters: ', e)
        }
      }
    },

    async loadInferences (projectId: string): Promise<Inference[]> {
      try {
        state.loading = true

        const { items: inferences } = await queryClient.fetchQuery(
          fetchInferencesQueryOptions({ projectId }),
        )

        state.inferences.set(projectId, inferences)

        return inferences
      } finally {
        state.loading = false
      }
    },

    async loadInferencesDependencies (inferenceIds: string[]): Promise<void> {
      await Promise.all(inferenceIds.map(id => this.loadInferenceDependencies(id, true)))
    },

    removeFromLocalStorage (inferenceId: string): void {
      const startedInferences: Record<string, string> = LocalStorage.getItem(LocalStorageKeys.Inference) ?? {}

      const inferences = Object.fromEntries(
        Object.entries(startedInferences)
          .filter(([id]) => id !== inferenceId),
      )

      LocalStorage.set(LocalStorageKeys.Inference, inferences)
    },

    async renameInference (projectId: string, inferenceId: string, name: string): Promise<Inference> {
      const { data } = await hubApi.deployments.updateDeployment(inferenceId, { name })
      const newInference = Inference.fromResponse(data)

      state.inferences.set(
        projectId,
        addOrReplaceBy(
          state.inferences.get(projectId) ?? [],
          newInference,
          v => v.id === newInference.id,
        ),
      )
      return newInference
    },

    saveToLocalStorage (inferenceId: string): void {
      const startedInferences: Record<string, string> = LocalStorage.getItem(LocalStorageKeys.Inference) ?? {}

      // remove all inferences, started more than 15 mins ago
      const inferences = Object.fromEntries(
        Object.entries(startedInferences)
          .filter(([, value]) => {
            const started = parseISO(value)
            const difference = differenceInMinutes(new Date(), started)
            return difference < 15
          }),
      )

      LocalStorage.set(LocalStorageKeys.Inference, {
        ...inferences,
        [inferenceId]: new Date(),
      })
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async startInference (projectId: string, inferenceId: string): Promise<void> {
      try {
        state.loading = true

        const response = await hubApi.deployments.startDeployment(inferenceId)

        if (response.data) {
          const inference = Inference.fromResponse(response.data)

          state.inferences.set(
            projectId,
            addOrReplaceBy(
              state.inferences.get(projectId) ?? [],
              inference,
              v => v.id === inference.id,
            ),
          )

          this.saveToLocalStorage(inferenceId)
        }
      } catch (e) {
        state.loading = false
        throw e
      }
    },

    async stopInference (projectId: string, inferenceId: string): Promise<void> {
      try {
        state.loading = true

        const { data } = await hubApi.deployments.stopDeployment(inferenceId)
        const inference = Inference.fromResponse(data)

        state.inferences.set(
          projectId,
          addOrReplaceBy(
            state.inferences.get(projectId) ?? [],
            inference,
            v => v.id === inference.id,
          ),
        )

        this.removeFromLocalStorage(inferenceId)
      } catch (e) {
        state.loading = false
        throw e
      }
    },

    async upsertInference ({
      deploymentDefinitionId,
      inference,
      jobData,
      projectId,
    }: {
      deploymentDefinitionId?: string
      inference?: Inference
      jobData: InferenceJobData
      projectId: string
    }): Promise<Inference> {
      const { getAlgorithmDefinition } = useAlgorithmsStore()
      const { getModelResource } = useModels()
      const { getProject } = useProjectsStore()

      const project = getProject(projectId)
      if (!project) throw new Error(`Unknown project ${projectId}`)

      if (!project.defaultAlgorithmId) throw new Error(`Project ${projectId} does not have a default algorithm`)
      if (!project.defaultAlgorithmVersion) throw new Error(`Project ${projectId} does not have a default algorithm`)

      // An existing inference can use an older version of the algorithm.
      // A new inference always uses the latest version of the algorithm.
      const algorithmId = inference?.algorithmId ?? project.defaultAlgorithmId
      const algorithmVersion = inference?.algorithmVersion ?? project.defaultAlgorithmVersion
      const algorithmDefinition = getAlgorithmDefinition(algorithmId, algorithmVersion)
      if (!algorithmDefinition) {
        throw new Error(`Unknown algorithm ${algorithmId} version ${algorithmVersion}`)
      }

      const deploymentDefinition = deploymentDefinitionId
        ? algorithmDefinition.getDeploymentDefinitionById(deploymentDefinitionId)
        : algorithmDefinition.deployment
      if (!deploymentDefinition) {
        throw new Error(
          `Unknown job Inference in algorithm ${algorithmId} version ${algorithmVersion}`,
        )
      }

      const models = jobData.models
        ? Object.entries(jobData.models).reduce((acc, [dependencyId, modelId]) => ({
          ...acc,
          [dependencyId]: modelId ? getModelResource(modelId) : undefined,
        }), {})
        : undefined

      const exampleStream = await this.getJobDataExampleStream(jobData)

      const dependencies: HubDeploymentDependencyRefs = await buildJobDependencies({
        exampleStream,
        jobData,
        jobDefinition: deploymentDefinition,
        models,
        projectId,
      })

      let newInference: Inference

      if (inference) {
        const startJobData: HubUpdateDeployment = {
          dependencies,
          name: jobData.name,
          runtimeParameters: buildRuntimeParameters(jobData),
        }

        const { data } = await hubApi.deployments.updateDeployment(inference?.id, startJobData)
        newInference = Inference.fromResponse(data)
      } else {
        const startJobData: HubCreateDeployment = {
          definitionId: deploymentDefinition.id,
          dependencies,
          name: jobData.name,
          projectId,
          runtimeParameters: buildRuntimeParameters(jobData),
        }

        const { data } = await hubApi.deployments.createDeployment(startJobData)
        newInference = Inference.fromResponse(data)
      }

      state.inferences.set(
        projectId,
        addOrReplaceBy(
          state.inferences.get(projectId) ?? [],
          newInference,
          v => v.id === newInference.id,
        ),
      )
      return newInference
    },
  }

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  }
})
